import { Send } from 'lucide-react';
import type { SentEmailStats } from '../../services/usageApi';
import { StatTile } from './StatTile';
import { TimeSeriesChart } from './TimeSeriesChart';
import { RankedBars } from './RankedBars';
import { SERIES_PRIMARY, formatCompact } from './chartTokens';

/**
 * What the board actually sent candidates, and when.
 *
 * From the sent-email RECORDS rather than events, so the counts are exact — an email that went out
 * has a row whether or not an analytics event survived the trip. Counts by kind, stage and day;
 * no recipient, subject or sender appears here or should ever be added.
 */
export function SentEmailSection({ stats }: { stats: SentEmailStats }) {
  if (stats.totals.sent === 0) {
    return (
      <div className="rounded-xl border border-dashed border-zinc-300 bg-white p-8 text-center">
        <Send className="mx-auto h-8 w-8 text-zinc-300" />
        <p className="mt-3 text-sm font-medium text-zinc-700">No emails sent in this range</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/*
        Deliberately just the total and the split by kind.
        "Sending days" and "with an attachment" were here and have been removed: neither answered a
        question anyone asks, and a tile nobody can act on trains people to skim the whole row.
        Response TIME is not here either — these are board-initiated emails (rejections, withdrawal
        notices) that answer no incoming message, so there is no "received" to measure from. The
        received → sent figure lives in Candidate requests, where an inbound mail exists to start
        the clock.
      */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-2">
        <StatTile label="Emails sent" value={formatCompact(stats.totals.sent)} hint="To candidates" />
        <StatTile
          label="Rejections"
          value={formatCompact(stats.byKind.find((k) => k.kind === 'rejection')?.count ?? 0)}
          hint="Of the total above"
        />
      </div>

      <section className="rounded-xl border border-zinc-200 bg-white p-5">
        <h3 className="text-sm font-semibold text-zinc-900">Emails sent per day</h3>
        <p className="mt-0.5 text-xs text-zinc-500">
          By the day they went out. Spikes usually mean a batch of rejections after a round closed.
        </p>
        <div className="mt-4">
          <TimeSeriesChart
            points={stats.daily.map((d) => ({ date: d.date, value: d.sent }))}
            color={SERIES_PRIMARY}
            seriesLabel="Emails sent"
          />
        </div>
      </section>

      <div className="grid gap-4 lg:grid-cols-2">
        <section className="rounded-xl border border-zinc-200 bg-white p-5">
          <h3 className="text-sm font-semibold text-zinc-900">What kind</h3>
          <p className="mt-0.5 text-xs text-zinc-500">Rejections, invites and the rest.</p>
          <div className="mt-3">
            <RankedBars
              items={stats.byKind.map((k) => ({ label: k.label, value: k.count }))}
              color={SERIES_PRIMARY}
            />
          </div>
        </section>
        <section className="rounded-xl border border-zinc-200 bg-white p-5">
          <h3 className="text-sm font-semibold text-zinc-900">From which stage</h3>
          <p className="mt-0.5 text-xs text-zinc-500">
            Where in the process candidates hear from us.
          </p>
          <div className="mt-3">
            <RankedBars
              items={stats.byStage.map((s) => ({ label: s.stage, value: s.count }))}
              color={SERIES_PRIMARY}
            />
          </div>
        </section>
      </div>
    </div>
  );
}
